import './Transactions.css';
import React, { Suspense } from 'react';
import LoadingScreen from './LoadingScreen';
import {createLinkFormatter, createLookupFormatter, createNumberFormatter, minMaxFilterEditor, minMaxFilterFunction} from './TableUtils.js';
import { useSearchParams, defer, Await, useLoaderData } from "react-router-dom";

import 'react-tabulator/lib/styles.css';
import 'react-tabulator/lib/css/tabulator.min.css';
import { ReactTabulator } from 'react-tabulator'

let _rangesCache = null;
const getRanges = async () => {
  if (_rangesCache) return _rangesCache;
  const url = (process.env.NODE_ENV === 'development' ? '/ranges.json': '/ranges');
  _rangesCache = await fetch(url).then((res) => res.json());
  return _rangesCache;
}

export const loader = async ({ params }) => {
  if (_rangesCache) return {ranges: _rangesCache};
  const ranges = getRanges();
  return defer({ranges});
}

function searchParamsToFilter(searchParams) {
  const filters = {};
  searchParams.forEach((value, key) => {
    if (key === "id" || key === "note") {
      filters[key] = {field: key, value: value, type: "like"};
    } else if (key === "portfolio_id" || key === "account" || key === "symbol" || key === "create_process" || key === "type" || key === "recording_account" || key === "other_account" || key === "other_symbol" || key === "day") {
      filters[key] = {field: key, value: value, type: "="};
    } else if (key === "min_quantity" || key === "min_other_quantity") {
      const newKey = key.substring(4);
      const newValue = filters[newKey] ? filters[newKey] : {field: newKey, value: {}};
      newValue.value.start = value;
      filters[newKey] = newValue;
    } else if (key === "max_quantity" || key === "max_other_quantity") {
      const newKey = key.substring(4);
      const newValue = filters[newKey] ? filters[newKey] : {field: newKey, value: {}};
      newValue.value.end = value;
      filters[newKey] = newValue;
    }
  });
  const result = [];
  for (var key in filters) {
    const value = filters[key];
    result.push(value);
  };
  return result;
}

function filterToSearchParams(filter) {
  const result = {};
  filter.forEach((row) => {
    const key = row.field;
    if (key === "id" || key === "note" || key === "portfolio_id" || key === "account" || key === "symbol" || key === "create_process" || key === "type" || key === "recording_account" || key === "other_account" || key === "other_symbol" || key === "day") {
      result[key] = row.value;
    } else if (key === "quantity" || key === "other_quantity") {
      if (row.value['start']) {
        result["min_" + key] = row.value['start'];
      }
      if (row.value['end']) {
        result["max_" + key] = row.value['end'];
      }
    }
  });
  return result;
}

function tabulatorUrlParams(params, prefix) {
  var parts = [];
  for (var key in params) {
    var value = params[key];
    var fullKey = prefix ? prefix + "[" + key + "]" : key;
    if (typeof value === "object" && value !== null) {
      parts.push(tabulatorUrlParams(value, fullKey));
    } else {
      parts.push(encodeURIComponent(fullKey) + "=" + encodeURIComponent(value));
    }
  }
  return parts.filter(Boolean).join("&");
}

const _txnAjaxCache = new Map();

function cachedAjaxRequestFunc(url, config, params) {
  var queryString = tabulatorUrlParams(params);
  var fullUrl = queryString ? url + '?' + queryString : url;
  if (_txnAjaxCache.has(fullUrl)) {
    return Promise.resolve(_txnAjaxCache.get(fullUrl));
  }
  return fetch(fullUrl)
    .then(function(res) { return res.json(); })
    .then(function(data) {
      _txnAjaxCache.set(fullUrl, data);
      return data;
    });
}

const filterClickFn = (field) => (cell) => {
  var hash = window.location.hash.substring(1);
  var qIndex = hash.indexOf('?');
  var currentParams = new URLSearchParams(qIndex >= 0 ? hash.substring(qIndex + 1) : '');
  if (currentParams.get(field) === cell.getValue()) {
    currentParams.delete(field);
  } else {
    currentParams.set(field, cell.getValue());
  }
  var qs = currentParams.toString();
  window.location.hash = '/transactions' + (qs ? '?' + qs : '');
};

function clearableListValues(arr) {
  return {"": "-- All --", ...Object.fromEntries(arr.map(v => [v, v]))};
}

function Transactions() {
  const loaderData = useLoaderData();
  const [searchParams] = useSearchParams();

  const columns = (ranges) => {
    const portfolioLookup = {};
    ranges['portfolios'].forEach((p) => { portfolioLookup[p.portfolio_id] = p.name; });

    return [
      { title: "portfolio_id", field: "portfolio_id", headerFilter:'list', headerFilterFunc:"=", headerFilterPlaceholder: "-- All --", headerFilterParams: { values: {"": "-- All --", ...Object.fromEntries(ranges['portfolios'].map(p => [p.portfolio_id, p.name]))} }, formatter: createLookupFormatter(portfolioLookup), },
      { title: "account", field: "account", headerFilter:'list', headerFilterFunc:"=", headerFilterPlaceholder: "-- All --", headerFilterParams:{values:clearableListValues(ranges['accounts'])},
        formatter: createLinkFormatter((cell) => `/transactions?account=${cell.getValue()}`, filterClickFn('account')), },
      { title: "symbol", field: "symbol", headerFilter:'list', headerFilterFunc:"=", headerFilterPlaceholder: "-- All --", headerFilterParams:{values:clearableListValues(ranges['symbols'])},
        formatter: createLinkFormatter((cell) => `/transactions?symbol=${cell.getValue()}`, filterClickFn('symbol')), },
      { title: "day", field: "day", headerSortStartingDir:"desc", sorter:"date",  headerFilter:'date'},
      { title: "quantity", field: "quantity", sorter:"number", headerFilter:minMaxFilterEditor, headerFilterFunc:minMaxFilterFunction, headerFilterLiveFilter:false, formatter: createNumberFormatter(2), hozAlign:'right', },
      { title: "other_account", field: "other_account", headerFilter:'list', headerFilterFunc:"=", headerFilterPlaceholder: "-- All --", headerFilterParams:{values:clearableListValues(ranges['accounts'])},
        formatter: createLinkFormatter((cell) => `/transactions?other_account=${cell.getValue()}`, filterClickFn('other_account')), },
      { title: "other_symbol", field: "other_symbol", headerFilter:'list', headerFilterFunc:"=", headerFilterPlaceholder: "-- All --", headerFilterParams:{values:clearableListValues(ranges['symbols'])},
        formatter: createLinkFormatter((cell) => `/transactions?other_symbol=${cell.getValue()}`, filterClickFn('other_symbol')), },
      { title: "other_quantity", field: "other_quantity", headerFilter:minMaxFilterEditor, headerFilterFunc:minMaxFilterFunction, headerFilterLiveFilter:false, formatter: createNumberFormatter(2), hozAlign:'right', },
      { title: "recording_account", field: "recording_account", headerFilter:'list', headerFilterFunc:"=", headerFilterPlaceholder: "-- All --", headerFilterParams:{values:clearableListValues(ranges['accounts'])},
        formatter: createLinkFormatter((cell) => `/transactions?recording_account=${cell.getValue()}`, filterClickFn('recording_account')), },
      { title: "type", field: "type", headerFilter:'list', headerFilterFunc:"=", headerFilterPlaceholder: "-- All --", headerFilterParams:{values:clearableListValues(ranges['transaction_types'])}, },
      { title: "id", field: "id", headerFilter:"input",
        formatter: createLinkFormatter((cell) => {
          const rawTxnId = String(cell.getValue()).split('|')[0];
          return `/raw_transaction/${rawTxnId}`;
        }),
      },
      { title: "create_process", field: "create_process", headerFilter:'list', headerFilterFunc:"=", headerFilterPlaceholder: "-- All --", headerFilterParams:{values:clearableListValues(ranges['create_processes'])},
        formatter: createLinkFormatter((cell) => `/parsing/${cell.getValue()}`), },
      { title: "create_date", field: "create_date", },
      { title: "update_date", field: "update_date", },
      { title: "note", field: "note", headerFilter:"input", },
    ];
  };

  const initialFilter = searchParamsToFilter(searchParams);

  const options = {
    ajaxURL: (process.env.NODE_ENV === 'development' ? '/transactions.json' : '/transactions'),
    ajaxRequestFunc: cachedAjaxRequestFunc,
    ajaxResponse: (url, params, response) => { return response },
    ajaxError: function (error) { console.log('ajaxError', error); },
    pagination: true,
    paginationMode: 'remote',
    sortMode: 'remote',
    initialSort:[
      {column:"day", dir:"desc"},
      {column:"id", dir:"asc"}
    ],
    filterMode: 'remote',
    initialHeaderFilter: initialFilter,
    layout:"fitDataStretch",
    height:"100%",
  };

  const dataFilteredHandler = (filters, rows) => {
    const newParams = filterToSearchParams(filters);
    const newSearch = new URLSearchParams(newParams).toString();
    const newHash = '#/transactions' + (newSearch ? '?' + newSearch : '');
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  };

  const renderTable = (ranges) => {
    return <ReactTabulator
             key={searchParams.toString()}
             options={options}
             columns={columns(ranges)}
             events={{dataFiltered: dataFilteredHandler}}
           />;
  }

  return (
    <Suspense fallback={<LoadingScreen visible="true" />}>
      <Await resolve={loaderData.ranges}
             errorElement={<div>Error Loading</div>}>
        {(ranges) => (
          <div className="transactions">
            { renderTable(ranges) }
          </div>
        )}
      </Await>
    </Suspense>
  );

}

export default Transactions;
